import { create } from "zustand";
import { isNullOrEmpty, isNullOrEmptyString } from "../../helpers";
import { Rate } from "../../components/pricing/types/RateTypes";
import { Hotel, RatePlan, RoomType } from "../../api/types/PricingApiTypes";

interface PricingState {
  hotels: Array<Hotel>;
  roomTypes: Array<RoomType>;
  ratePlans: Array<RatePlan>;
  rates: Array<Rate>;
  selectedHotelId: string | number;
  selectedRoomId: string | number;
  selectedRatePlanId: string | number;
  setHotels: (hotels: Array<Hotel>) => void;
  setHotelId: (hotelId: string | number) => void;
  setRoomTypes: (roomTypes: Array<RoomType>) => void;
  setRoomId: (roomTypeId: string | number) => void;
  setRatePlans: (ratePlans: Array<RatePlan>) => void;
  setRatePlanId: (ratePlanId: string | number) => void;
  setRates: (rates: Array<Rate>) => void;
  updateRate: (rate: Rate) => void;
}

const validateRate = (rate: Rate) => {
  let isValid = true;
  let errors = new Array<string>();

  if (rate.isModified) {
    if (isNullOrEmptyString(rate.bookingCode)) {
      errors.push(
        "Booking code is required for any other changes to be saved and take effect."
      );
    }
    if (rate.quantity < 0) {
      errors.push("Quantity must have a value greater than or equal to 0.");
    }
    if (rate.minNights < 0) {
      errors.push("Min Nights must have a value greater than or equal to 0.");
    }
    if (rate.cost <= 0) {
      errors.push("Rate must have a cost greater than 0.");
    }
    if (rate.quantity > 0 && rate.isFreeSell) {
      errors.push(
        "Cannot have a quantity greater than zero and have free-sell checked."
      );
    }
  }

  isValid = errors.length === 0;
  rate.isValid = isValid;
  rate.errors = errors;

  return { isValid, errors };
};

const usePricingStore = create<PricingState>()((set) => ({
  hotels: [],
  roomTypes: [],
  ratePlans: [],
  rates: [],
  selectedHotelId: "",
  selectedRoomId: "",
  selectedRatePlanId: "",
  setHotels: (hotels: Array<Hotel>) => {
    set((state) => ({
      hotels,
      selectedHotelId: isNullOrEmpty(hotels) ? "" : hotels[0].hotelId,
    }));
  },
  setHotelId: (hotelId: string | number) =>
    set({
      selectedHotelId: hotelId ?? "",
      selectedRoomId: "",
      selectedRatePlanId: "",
      rates: [],
    }),

  setRoomTypes: (roomTypes: Array<RoomType>) => {
    set((state) => ({
      roomTypes,
      selectedRoomId: isNullOrEmpty(roomTypes) ? "" : roomTypes[0].roomTypeId,
    }));
  },
  setRoomId: (roomTypeId: string | number) =>
    set({
      selectedRoomId: roomTypeId ?? "",
      selectedRatePlanId: "",
      rates: [],
    }),

  setRatePlans: (ratePlans: Array<RatePlan>) => {
    set((state) => ({
      ratePlans,
      selectedRatePlanId: isNullOrEmpty(ratePlans)
        ? ""
        : ratePlans[0].ratePlanId,
    }));
  },
  setRatePlanId: (ratePlanId: string | number) =>
    set({
      selectedRatePlanId: ratePlanId ?? "",
      rates: [],
    }),

  setRates: (rates: Array<Rate>) =>
    set({
      rates: rates.map((rate, rateIndex) => {
        let innerRate = Object.assign(
          {},
          { isModified: false, id: rateIndex },
          rate
        );
        validateRate(innerRate);

        return innerRate;
      }),
    }),
  updateRate: (rate: Rate) =>
    set((state) => ({
      rates: state.rates.map((existingRate) => {
        let innerRate =
          existingRate.id === rate.id
            ? Object.assign({}, existingRate, rate, { isModified: true })
            : existingRate;
        validateRate(innerRate);

        return innerRate;
      }),
    })),
}));

export { usePricingStore };
